import Dialog from '@mui/material/Dialog';
import AppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import CloseIcon from '@mui/icons-material/Close';
import Slide from '@mui/material/Slide';
import { Fragment, forwardRef, useContext, useEffect, useRef, useState } from 'react';
import { Accordion, AccordionDetails, AccordionSummary, Box, Button, Checkbox, Chip, CircularProgress, colors, Container, FormControlLabel, Paper, Snackbar, Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import DownloadIcon from '@mui/icons-material/Download';
import { toast } from 'react-toastify';
import { v4 as uuidv4 } from 'uuid';
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, horizontalListSortingStrategy, useSortable, arrayMove } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import html2canvas from 'html2canvas';
import { saveAs } from 'file-saver';
import domtoimage from 'dom-to-image';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import zeroDeductibleUniform from '../utils/zeroDeductibleUniform';
import CopyImageButton from './CopyImageButton';
import CopyExcelDataButton from './CopyExcelDataButton';
import AddTobDialog from './AddTobDialog';
import { EMAIL_STYLES, getLiveTableHTML } from './EmailFormDialog';
import { downloadQuickQuotationPdf } from '../services/AutoQuotationService';
import TOBTable, { FIRST_COLUMN_WIDTH as TOB_FIRST_COLUMN_WIDTH } from './auto_quotation/TOBTable';
import TOBModuleOptions from './auto_quotation/TOBModuleOptions';
import TOBTableMenuBar from './auto_quotation/TOBTableMenuBar';
import { normalizeAreaOfCoverDisplay } from './auto_quotation/QuotationPreviewTable';
import { DEFAULT_TOB_LANGUAGE, fetchTOBBenefits } from '../services/TOBService';
import { TOB_MODULES, isUniversalTobId, normalizeTobId, setTobTableRowValue } from '../utils/tobPlanIds';
import { isBoldCell, patchCell, patchCellStyle, readCell } from '../utils/tobTableEditing';
import { AuthContext } from '../utils/AuthContext';

const Transition = forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

function SortablePlanChip({ id, label, onDelete }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    cursor: isDragging ? 'grabbing' : 'grab',
  };

  return (
    <div ref={setNodeRef} style={style} {...attributes} {...listeners}>
      <Chip size="small"
        label={label}
        onDelete={onDelete}
        sx={{ backgroundColor: '#7e57c2', color: '#fff', cursor: 'inherit', '& .MuiChip-deleteIcon': { color: 'rgba(255,255,255,0.7)', '&:hover': { color: '#fff' } } }}
      />
    </div>
  );
}

export default function PremiumsSummaryDialog({ open, setOpen, clients, response, selectedPlans, allSelectedPlans = [], currency, deductibles,
  discounts, insurers_plans_map, computedRates, paymentFrequency,
  specificAreaOfCoverage, specificPaymentFrequency, insurers, selectedOptions, onPlanCountChange }) {

  const [showImageCopy, setShowImageCopy] = useState(false);
  const [showDeductible, setShowDeductible] = useState(true);
  // Column order chosen by dragging the plan or TOB chips, holding plan keys and added TOBs' uids
  // alike since each plan's TOB is its column in the TOB table; anything not in it keeps its
  // default order after it
  const [planOrder, setPlanOrder] = useState([]);
  const plansSensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));
  const tobPlansSensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

  const { auth } = useContext(AuthContext);
  const [tobExpanded, setTobExpanded] = useState(false);
  // The TOB is read only until the edit icon beside its title is switched on
  const [tobEditing, setTobEditing] = useState(false);
  // Set while the PDF waits for the TOBs, which only load once their accordion is opened
  const [pdfRequested, setPdfRequested] = useState(false);
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  // TOB id => { table, vertical_merge_cells } as loaded from universal_output, or null when it
  // couldn't be loaded; 'benefit_labels' holds the label column
  const [tobTables, setTobTables] = useState({});
  const pendingTobFetchesRef = useRef(new Set());
  // TOBs added from the Add TOB chip, which belong to no plan
  const [addedTobs, setAddedTobs] = useState([]);
  // Plan key => the TOB picked from a placeholder's Add button, for plans with no TOB of their own
  const [filledTobs, setFilledTobs] = useState({});
  // TOB uid => { row number => cell fields changed by editing or the menu bar }, and TOB uid =>
  // its vertical_merge_cells once merged/unmerged, kept apart so a reloaded table keeps them
  const [tobCellPatches, setTobCellPatches] = useState({});
  const [tobMergeOverrides, setTobMergeOverrides] = useState({});
  // { planUid, rows: ['7', '8', ...] } -- planUid holds a TOB's uid, always within one column.
  const [tobSelection, setTobSelection] = useState(null);
  // The formatting bar only shows while the TOB table is hovered and has a selection.
  const [tobTableHovered, setTobTableHovered] = useState(false);
  const [selectedTobModules, setSelectedTobModules] = useState(() => TOB_MODULES.map(module => module.name));
  const [addTobOpen, setAddTobOpen] = useState(false);
  const [tobPlaceholderToFill, setTobPlaceholderToFill] = useState(null);

  const handleClose = () => {
    setOpen(false);
  };

  function handleCloseSnackbar() {
    setShowImageCopy(false);
  }


  function getRate(selectedPlan, index) {
    if (isMyHealthComparison(selectedPlan)) {
      return getMyHealthComparison(selectedPlan, index)?.[getBasePlan(selectedPlan)] ?? '';
    }

    if (getPlanInsurer(selectedPlan) === 'bhp_indonesia') {
      return getBhpIndonesiaRate(selectedPlan, index);
    }

    const insurer = insurers_plans_map[selectedPlan].insurer;
    const plan = selectedPlan.replace(insurers_plans_map[selectedPlan].insurer + '_', '');

    // if (!response || !response[insurer] || !response[insurer].rates[index] || !response[insurer].rates[index][plan]) {
    //   return '';
    // }

    // const totalPercent = (100 - discounts[insurer]) * 0.01;

    // return response[insurer].rates[index][plan] * totalPercent;

    if (!computedRates || !computedRates[insurer] || !computedRates[insurer][index] || !computedRates[insurer][index][plan]) {
      return '';
    }

    return withAssistanceCovers(insurer, computedRates[insurer][index], computedRates[insurer][index][plan]);
  }

  // Lifeline's, Company's and Oil & Gas's Evacuation/Repatriation checkboxes are optional add-ons
  // persisted in selectedOptions; when checked, their rates are added on top of each plan tier.
  const ASSISTANCE_COVER_INSURERS = ['lifeline', 'company', 'oil_gas'];
  const ASSISTANCE_COVERS = { evacuation: 'Evacuation', repatriation: 'Repatriation' };

  function getSelectedAssistanceCovers(insurer) {
    if (!ASSISTANCE_COVER_INSURERS.includes(insurer)) {
      return [];
    }

    return Object.keys(ASSISTANCE_COVERS).filter((cover) => {
      const option = (selectedOptions || []).find((o) => o.insurer === insurer && o.type === cover);

      return !!option && option.option_value == 1;
    });
  }

  function withAssistanceCovers(insurer, clientRate, rate) {
    if (typeof rate !== 'number') {
      return rate;
    }

    let total = rate;

    getSelectedAssistanceCovers(insurer).forEach((cover) => {
      const value = clientRate?.[cover];

      if (typeof value === 'number' && !isNaN(value)) {
        total += value;
      }
    });

    return total;
  }

  // BHP Indonesia's Dental & Optical options are alternative levels added on top of each tier,
  // so each tier is shown once per selected option, keyed '<tier plan>__<dental plan>'. These
  // options are filtered out of selectedPlans, hence allSelectedPlans.
  // With Maternity is chosen per client (add_ons_per_client in selectedOptions) and swaps
  // Premier/Elite/Ultimate's rate for its '_with_maternity' rate, mirroring BHP_Indonesia_Rates.
  const BHP_INDONESIA_DENTAL_OPTIONS = [
    { planId: 'bhp_indonesia_dental_1', rateKey: 'dental_1', label: 'Dental 1' },
    { planId: 'bhp_indonesia_dental_optical_2', rateKey: 'dental_optical_2', label: 'Dental & Optical 2' },
    { planId: 'bhp_indonesia_dental_optical_3', rateKey: 'dental_optical_3', label: 'Dental & Optical 3' },
  ];
  const BHP_INDONESIA_MATERNITY_PLANS = ['premier', 'elite', 'ultimate'];

  function getBasePlan(selectedPlan) {
    return selectedPlan.split('__')[0];
  }

  // MyHEALTH's comparison tables (their Compare button) are each shown as an extra column
  // after the main one, keyed '<insurer>__compare_<id>'. computedRates carries their totals per
  // client under 'comparisons'.
  const MY_HEALTH_COMPARISON_INSURERS = ['my_health_singapore', 'my_health_indonesia', 'my_health_philippines'];
  const MY_HEALTH_COMPARISON_SEPARATOR = '__compare_';

  function isMyHealthComparison(selectedPlan) {
    return typeof selectedPlan === 'string' && selectedPlan.includes(MY_HEALTH_COMPARISON_SEPARATOR);
  }

  function getMyHealthComparisons(insurer, index = 0) {
    return computedRates?.[insurer]?.[index]?.comparisons || [];
  }

  function getMyHealthComparison(selectedPlan, index = 0) {
    const comparisonId = Number(selectedPlan.split(MY_HEALTH_COMPARISON_SEPARATOR)[1]);

    return getMyHealthComparisons(getBasePlan(selectedPlan), index).find((comparison) => comparison.id === comparisonId) || null;
  }

  function getMyHealthComparisonSubtotal(selectedPlan) {
    const clientRates = computedRates?.[getBasePlan(selectedPlan)];

    if (!clientRates) {
      return '';
    }

    let total = 0;

    for (let i = 0; i < clientRates.length; i++) {
      const rate = getRate(selectedPlan, i);

      if (typeof rate !== 'number' || isNaN(rate)) {
        return '';
      }

      total += rate;
    }

    return total;
  }

  function getBhpIndonesiaDentalOption(selectedPlan) {
    const dentalPlanId = selectedPlan.split('__')[1];

    return BHP_INDONESIA_DENTAL_OPTIONS.find((option) => option.planId === dentalPlanId) || null;
  }

  function getBhpIndonesiaAddOnsPerClient() {
    const option = (selectedOptions || []).find((o) => o.insurer === 'bhp_indonesia' && o.type === 'add_ons_per_client');

    try {
      return JSON.parse(option?.option_value || '{}') || {};
    } catch (e) {
      return {};
    }
  }

  function hasBhpIndonesiaMaternity(clientIdx) {
    const isChecked = getBhpIndonesiaAddOnsPerClient()[clientIdx]?.maternity;

    if (isChecked !== undefined) {
      return isChecked;
    }

    const client = clients[clientIdx];

    return client?.gender === 'Female' && client.age >= 18 && client.age <= 64;
  }

  function getBhpIndonesiaRate(selectedPlan, index) {
    const rates = computedRates?.['bhp_indonesia']?.[index];

    if (!rates) {
      return '';
    }

    const plan = insurers_plans_map[getBasePlan(selectedPlan)].plan;
    const withMaternity = BHP_INDONESIA_MATERNITY_PLANS.includes(plan) && hasBhpIndonesiaMaternity(index);
    let rate = rates[withMaternity ? plan + '_with_maternity' : plan];

    if (typeof rate !== 'number' || isNaN(rate)) {
      return rate;
    }

    const dental = getBhpIndonesiaDentalOption(selectedPlan);
    const dentalRate = dental ? rates[dental.rateKey] : undefined;

    if (typeof dentalRate === 'number' && !isNaN(dentalRate)) {
      rate += dentalRate;
    }

    return rate;
  }

  function getBhpIndonesiaSubtotal(selectedPlan) {
    if (!computedRates || !computedRates['bhp_indonesia']) {
      return '';
    }

    let total = 0;

    for (let i = 0; i < computedRates['bhp_indonesia'].length; i++) {
      const rate = getBhpIndonesiaRate(selectedPlan, i);

      if (typeof rate === 'number' && !isNaN(rate)) {
        total += rate;
      }
    }

    return total;
  }

  function getAddOnLabels(selectedPlan) {
    const coverLabels = getSelectedAssistanceCovers(getPlanInsurer(selectedPlan)).map((cover) => ASSISTANCE_COVERS[cover]);
    const dental = getBhpIndonesiaDentalOption(selectedPlan);

    return dental ? [...coverLabels, dental.label] : coverLabels;
  }

  function getCurrency(selectedPlan) {
    const insurer = getPlanInsurer(selectedPlan);

    if (!computedRates || !computedRates[insurer] || !computedRates[insurer][0] || !computedRates[insurer][0].currency) {
      return '';
    }

    // if (computedRates[insurer].length === 0) {
    //   return '';
    // }


    return computedRates[insurer][0].currency;
  }

  function getComputedRate(selectedPlan, index) {
    const insurer = insurers_plans_map[selectedPlan].insurer;
    const plan = selectedPlan.replace(insurers_plans_map[selectedPlan].insurer + '_', '');

    // if (!response || !response[insurer] || !response[insurer].rates[index] || !response[insurer].rates[index][plan]) {
    //   return '';
    // }

    // const totalPercent = (100 - discounts[insurer]) * 0.01;

    // return response[insurer].rates[index][plan] * totalPercent;

    if (!computedRates || !computedRates[insurer] || !computedRates[insurer][index] || !computedRates[insurer][index][plan]) {
      return '';
    }

    return computedRates[insurer][index];
  }

  // function getSubtotalRate(selectedPlan) {

  //   const insurer = insurers_plans_map[selectedPlan].insurer;
  //   const plan = selectedPlan.replace(insurers_plans_map[selectedPlan].insurer + '_', '');

  //   if (!response || !response[insurer]) {
  //     return '';
  //   }

  //   let total = 0;
  //   const rates = response[insurer].rates;

  //   for (let i = 0; i < rates.length; i++) {
  //     total += rates[i][plan];
  //   }

  //   const totalPercent = (100 - discounts[insurer]) * 0.01;

  //   return total * totalPercent;
  // }

  function getSubtotalRate(selectedPlan) {
    if (isMyHealthComparison(selectedPlan)) {
      return getMyHealthComparisonSubtotal(selectedPlan);
    }

    if (getPlanInsurer(selectedPlan) === 'bhp_indonesia') {
      return getBhpIndonesiaSubtotal(selectedPlan);
    }

    const insurer = insurers_plans_map[selectedPlan].insurer;
    const plan = selectedPlan.replace(insurers_plans_map[selectedPlan].insurer + '_', '');

    if (!computedRates || !computedRates[insurer]) {
      return '';
    }

    let total = 0;
    const clientRate = computedRates[insurer];

    for (let i = 0; i < clientRate.length; i++) {
      total += withAssistanceCovers(insurer, clientRate[i], clientRate[i][plan]);
    }

    return total;
  }

  function getDeductible(selectedPlan) {
    if (isCombo(selectedPlan)) {
      return getComboDeductible(selectedPlan);
    }

    if (isMyHealthComparison(selectedPlan)) {
      return getMyHealthComparison(selectedPlan)?.deductible + ' Deductible';
    }

    const insurer = getPlanInsurer(selectedPlan);

    if (insurer === 'lifeline') {
      return deductibles['lifeline'];
    } else if (insurer === 'company') {
      return deductibles['company']['label'];
    } else if (insurer === 'first_choice') {
      return deductibles['first_choice'];
    } else if (insurer === 'ihhp') {
      return deductibles['ihhp'];
    } else if (insurer === 'vumi') {
      return deductibles['vumi'];
    } else if (insurer === 'my_health_indonesia') {
      return deductibles['my_health_indonesia'] + ' Deductible';
    } else if (insurer === 'my_health_singapore') {
      return deductibles['my_health_singapore'] + ' Deductible';
    } else if (insurer === 'my_health_vietnam') {
      return deductibles['my_health_vietnam'] + ' Deductible';
    } else if (insurer === 'my_health_philippines') {
      return deductibles['my_health_philippines'] + ' Deductible';
    } else if (insurer === 'optimum_global') {
      return deductibles['optimum_global'];
    } else if (insurer === 'pro_medico') {
      return deductibles['pro_medico'];
    } else if (insurer === 'cigna_care_connect' || insurer === 'oil_gas') {
      return 'Nil Deductible';
    } else if (insurer === 'morgan_price') {
      // Morgan Price has no deductible, only an excess applicable to in-patient treatment
      const option = (selectedOptions || []).find((o) => o.insurer === 'morgan_price' && o.type === 'excess_inpatient_treatment');
      const excess = String(option?.option_value ?? '').trim();

      if (!excess) {
        return 'N/A';
      }

      if (/nil/i.test(excess) || Number(excess.replace(/[^0-9.]/g, '')) === 0) {
        return 'Nil Excess';
      }

      return /excess/i.test(excess) ? excess : excess + ' Excess';
    } else if (insurer === 'bhp_indonesia') {
      return deductibles['bhp_indonesia'];
    } else if (insurer === 'cigna_global_health_option') {
      return deductibles['cigna_global_health_option'];
    } else if (insurer === 'hsbc_smart_care_optimum_enhanced') {
      return deductibles['hsbc_smart_care_optimum_enhanced'];
    } else if (insurer === 'pacific_cross_primary_care_plan') {
      // Deductibles are kept per plan as a bare amount ('1000') or 'Nil'
      const plan = insurers_plans_map[selectedPlan].plan;
      const deductible = deductibles['pacific_cross_primary_care_plan']?.[plan];

      if (deductible && !isNaN(deductible)) {
        return getCurrency(selectedPlan) + ' ' + Number(deductible).toLocaleString('en-PH') + ' Deductible';
      }

      return deductible;
    } else if (insurer === 'pacific_cross_select') {
      // Select has no deductible, only a co-payment
      const option = (selectedOptions || []).find((o) => o.insurer === 'pacific_cross_select' && o.type === 'co_payment');

      return option?.option_value ? option.option_value + ' Co-Payment' : 'N/A';
    } else if (insurer === 'pacific_cross_blue_royale') {
      return deductibles['pacific_cross_blue_royale'];
    }


    return 'N/A';
  }

  function formatRate(selectedPlan, currency, rate) {
    if (isNaN(rate) || rate == 'Unavailable' || typeof rate === 'string') {
      return <span style={{ color: '#f44336' }}>{'Unavailable'}</span>
    }

    if (isNaN(rate)) {
      return rate;
    }

    let _currency = currency;

    if (getBasePlan(selectedPlan) === 'my_health_indonesia') {
      _currency = 'USD';
    }

    if (getBasePlan(selectedPlan) === 'my_health_singapore') {
      _currency = 'SGD';
    }

    if (selectedPlan === 'my_health_vietnam') {
      _currency = 'USD';
    }

    if (getBasePlan(selectedPlan) === 'my_health_philippines') {
      _currency = 'USD';
    }



    return _currency + ' ' + rate.toLocaleString('en-PH', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  }

  function sortedPlans() {
    const plans = selectedPlans;

    // Create an array of the keys in insurers_plans_map to use as the order reference
    const order = Object.keys(insurers_plans_map);

    // Sort the plans based on their index in the order array
    return plans.sort((a, b) => order.indexOf(a) - order.indexOf(b));
  }

  // BWHO's and IHHP's plan checkboxes are independent modules rather than a single tier, so
  // selectedPlans can contain several of their entries at once, and the user can save
  // several module combinations to compare via the "Add To Comparison" dialog in
  // BWHO_Rates / IHHP_Rates (persisted per-insurer in selectedOptions). Each saved
  // combination becomes its own column here, keyed by a synthetic '<insurer>_combo_<id>'
  // id since none of them are real selectedPlans entries. When nothing has been saved for
  // comparison yet, the current live checkbox selection is shown instead, as a single column.
  //
  // planId: the selectedPlans entry that marks the module as live (null = read from selectedOptions)
  // rateKey: the per-module (non-cumulative) field in computedRates
  // deductibleKey: BWHO keeps one deductible per module
  const COMBO_MODULES = {
    bwho: [
      { key: 'wmi', planId: 'bwho_wmi', rateKey: 'wmi_raw', label: 'WMI', deductibleKey: 'wmi_deductible' },
      { key: 'wmp', planId: 'bwho_wmp', rateKey: 'wmp_raw', label: 'WMP', deductibleKey: 'wmp_deductible' },
      { key: 'wme', planId: 'bwho_wme', rateKey: 'wme_raw', label: 'WME', deductibleKey: 'wme_deductible' },
      { key: 'ww', planId: 'bwho_ww', rateKey: 'ww_raw', label: 'WW', deductibleKey: 'ww_deductible' },
      { key: 'evacuation', planId: null, rateKey: 'evacuation', label: 'WE' },
    ],
    ihhp: [
      { key: 'hp', planId: 'ihhp_hospital_plan', rateKey: 'hospital_plan', label: 'HP' },
      { key: 'm1', planId: 'ihhp_non_hospitalisation_benefits', rateKey: 'non_hospitalisation_benefits', label: 'M1' },
      { key: 'm2', planId: 'ihhp_medicine_appliances', rateKey: 'medicine_appliances', label: 'M2' },
      { key: 'm3', planId: 'ihhp_medical_evacuation_repatriation', rateKey: 'medical_evacuation_repatriation', label: 'M3' },
      { key: 'm4a', planId: 'ihhp_dental_optical_a', rateKey: 'dental_optical_a', label: 'M4A' },
      { key: 'm4b', planId: 'ihhp_dental_optical_b', rateKey: 'dental_optical_b', label: 'M4B' },
    ],
  };

  function getComboInsurer(key) {
    if (typeof key !== 'string') {
      return null;
    }

    return Object.keys(COMBO_MODULES).find((insurer) => key.startsWith(insurer + '_combo_')) || null;
  }

  function isCombo(key) {
    return !!getComboInsurer(key);
  }

  function getPlanInsurer(selectedPlan) {
    return getComboInsurer(selectedPlan) || insurers_plans_map[getBasePlan(selectedPlan)].insurer;
  }

  function getSavedComparisons(insurer) {
    const option = (selectedOptions || []).find((o) => o.insurer === insurer && o.type === 'comparisons');

    let value = option?.option_value;

    // The rates components store comparisons as a JSON string
    if (typeof value === 'string') {
      try {
        value = JSON.parse(value);
      } catch (e) {
        value = [];
      }
    }

    return Array.isArray(value) ? value : [];
  }

  function isLiveOptionSelected(insurer, type) {
    const option = (selectedOptions || []).find((o) => o.insurer === insurer && o.type === type);

    return !!option && option.option_value == 1;
  }

  function getCombinations(insurer) {
    const saved = getSavedComparisons(insurer);

    if (saved.length > 0) {
      return saved.map((comparison) => ({ ...comparison, key: insurer + '_combo_' + comparison.id }));
    }

    if (!selectedPlans.some((plan) => plan.startsWith(insurer + '_'))) {
      return [];
    }

    const live = { key: insurer + '_combo_live' };

    COMBO_MODULES[insurer].forEach((module) => {
      live[module.key] = module.planId ? selectedPlans.includes(module.planId) : isLiveOptionSelected(insurer, module.key);
    });

    return [live];
  }

  function getComboRate(insurer, combo, index) {
    if (!computedRates || !computedRates[insurer] || !computedRates[insurer][index]) {
      return '';
    }

    // IHHP modules are only added on top of the Hospital Plan
    if (insurer === 'ihhp' && !combo.hp) {
      return 0;
    }

    const rates = computedRates[insurer][index];
    let total = 0;

    COMBO_MODULES[insurer].forEach((module) => {
      if (combo[module.key]) {
        const value = rates[module.rateKey];

        if (!isNaN(value)) {
          total += value;
        }
      }
    });

    return total;
  }

  function getComboSubtotal(insurer, combo) {
    if (!computedRates || !computedRates[insurer]) {
      return '';
    }

    let total = 0;

    for (let i = 0; i < computedRates[insurer].length; i++) {
      const rate = getComboRate(insurer, combo, i);

      if (!isNaN(rate)) {
        total += rate;
      }
    }

    return total;
  }

  function getSortedCombinations(insurer) {
    return [...getCombinations(insurer)].sort((a, b) => getComboSubtotal(insurer, a) - getComboSubtotal(insurer, b));
  }

  function getComboByKey(key) {
    return getSortedCombinations(getComboInsurer(key)).find((combo) => combo.key === key);
  }

  function getComboLabel(key) {
    const combo = getComboByKey(key);

    return COMBO_MODULES[getComboInsurer(key)].filter((module) => combo[module.key]).map((module) => module.label).join(' + ');
  }

  function getComboDeductible(key) {
    const insurer = getComboInsurer(key);

    if (insurer === 'ihhp') {
      return deductibles['ihhp'];
    }

    const combo = getComboByKey(key);

    const selectedDeductibles = COMBO_MODULES[insurer]
      .filter((module) => module.deductibleKey && combo[module.key])
      .map((module) => deductibles[insurer][module.deductibleKey]);

    return [...new Set(selectedDeductibles)].join('; ');
  }

  function getComboRateByKey(key, index) {
    return getComboRate(getComboInsurer(key), getComboByKey(key), index);
  }

  function getComboSubtotalByKey(key) {
    return getComboSubtotal(getComboInsurer(key), getComboByKey(key));
  }

  function getDisplayPlans() {
    let plans = sortedPlans();

    // Replace each combo insurer's plans with its combination columns, at the position of its first plan
    Object.keys(COMBO_MODULES).forEach((insurer) => {
      const insurerIndex = plans.findIndex((plan) => insurers_plans_map[plan]?.insurer === insurer);
      const otherPlans = plans.filter((plan) => insurers_plans_map[plan]?.insurer !== insurer);
      const comboKeys = getSortedCombinations(insurer).map((combo) => combo.key);

      if (insurerIndex === -1 || comboKeys.length === 0) {
        plans = otherPlans;
        return;
      }

      const insertAt = plans.slice(0, insurerIndex).filter((plan) => insurers_plans_map[plan]?.insurer !== insurer).length;

      plans = [...otherPlans.slice(0, insertAt), ...comboKeys, ...otherPlans.slice(insertAt)];
    });

    const bhpIndonesiaDentalPlanIds = BHP_INDONESIA_DENTAL_OPTIONS
      .map((option) => option.planId)
      .filter((planId) => allSelectedPlans.includes(planId));

    if (bhpIndonesiaDentalPlanIds.length > 0) {
      plans = plans.flatMap((plan) => insurers_plans_map[plan]?.insurer === 'bhp_indonesia'
        ? bhpIndonesiaDentalPlanIds.map((planId) => plan + '__' + planId)
        : [plan]);
    }

    plans = plans.flatMap((plan) => MY_HEALTH_COMPARISON_INSURERS.includes(plan)
      ? [plan, ...getMyHealthComparisons(plan).map((comparison) => plan + MY_HEALTH_COMPARISON_SEPARATOR + comparison.id)]
      : [plan]);

    return plans;
  }

  // function getCurrencySign() {
  //   if (currency === 'USD') {
  //     return '$';
  //   } else if (currency === 'EURO') {
  //     return '€';
  //   } else if (currency === 'GBP') {
  //     return '£';
  //   } else if (currency === 'SGD') {
  //     return 'SGD';
  //   }

  //   return '';
  // }

  function getPlanLevelRowSpan() {
    let rowSpan = 3;

    if (showDeductible) {
      rowSpan += 1;
    }

    return rowSpan;
  }

  function getInsurerLabel(selectedPlan) {
    return isCombo(selectedPlan) ? insurers_plans_map[COMBO_MODULES[getComboInsurer(selectedPlan)][0].planId].insurer_label : insurers_plans_map[getBasePlan(selectedPlan)].insurer_label;
  }

  function getPlanLabel(selectedPlan) {
    if (isCombo(selectedPlan)) {
      return getComboLabel(selectedPlan);
    }

    if (isMyHealthComparison(selectedPlan)) {
      return getMyHealthComparison(selectedPlan)?.plan_label;
    }

    if (MY_HEALTH_COMPARISON_INSURERS.includes(selectedPlan) || selectedPlan === 'my_health_vietnam') {
      return computedRates?.[selectedPlan]?.[0]?.['plan_label'] ?? '';
    }

    // Select Plus / Select Standard each carry their own Core Benefits (room type)
    if (getPlanInsurer(selectedPlan) === 'pacific_cross_select') {
      const plan = insurers_plans_map[selectedPlan].plan;
      const option = (selectedOptions || []).find((o) => o.insurer === 'pacific_cross_select' && o.type === plan + '_room_type');

      return insurers_plans_map[selectedPlan].plan_label + ' (' + (option?.option_value || 'Ward') + ')';
    }

    return [insurers_plans_map[getBasePlan(selectedPlan)].plan_label, ...getAddOnLabels(selectedPlan)].join(' + ');
  }

  function printPlansLabel() {
    return displayPlans.map(selectedPlan => {
      return <td key={selectedPlan}>{getPlanLabel(selectedPlan)}</td>;
    })
  }

  function sortByPlanOrder(keys) {
    const rank = (key) => {
      const index = planOrder.indexOf(key);

      return index === -1 ? planOrder.length : index;
    };

    return [...keys].sort((a, b) => rank(a) - rank(b));
  }

  function getOrderedPlans() {
    return sortByPlanOrder(getDisplayPlans());
  }

  // Moves a chip within its own row (plans, or TOBs), leaving the columns the row doesn't show
  // where they are in the shared order
  function moveColumn(columns, activeId, overId) {
    const moved = arrayMove(columns, columns.indexOf(activeId), columns.indexOf(overId));
    const movedSet = new Set(moved);
    let index = 0;

    setPlanOrder(tobColumns.map((key) => movedSet.has(key) ? moved[index++] : key));
  }

  function handlePlansDragEnd(event) {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    moveColumn(displayPlans, active.id, over.id);
  }

  function handleTobPlansDragEnd(event) {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    moveColumn(tobColumns, active.id, over.id);
  }

  function getAreaOfCoverage(selectedPlan) {
    const insurer = getPlanInsurer(selectedPlan);
    let area_of_coverage = specificAreaOfCoverage[insurer] || '';

    if (insurer === 'hsbc_smart_care_optimum_enhanced' && !area_of_coverage) {
      area_of_coverage = 'Singapore';
    }

    return area_of_coverage;
  }

  // The universal_output TOB column a plan's TOB is loaded from, or null when it has none (it
  // then shows a placeholder whose Add button picks one). Combinations are rebuilt into the TOB
  // ids AutoQuotation uses: BWHO's from its modules, IHHP's from its highest module.
  // A MyHEALTH plan's TOB id, built from its label ("HS Elite + OP Extensive") the way the
  // MyHEALTH compact rates dialogs build their plan id ("my_health_philippines_elite_op_extensive")
  function getMyHealthTobId(selectedPlan) {
    const toToken = (value) => value.trim().toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, '');
    const parts = (getPlanLabel(selectedPlan) || '').split('+').map((part) => part.trim());
    const hsPlan = parts.shift()?.match(/^HS\s+(.+)$/)?.[1];

    if (!hsPlan) {
      return null;
    }

    const moduleTokens = parts.map((part) => {
      const [module, ...tier] = part.split(/\s+/);

      return module.toLowerCase() + '_' + toToken(tier.join(' '));
    });

    return [getBasePlan(selectedPlan), toToken(hsPlan), ...moduleTokens].join('_');
  }

  function getPlanTobId(selectedPlan) {
    let tobId;

    if (isCombo(selectedPlan)) {
      const insurer = getComboInsurer(selectedPlan);
      const combo = getComboByKey(selectedPlan);
      const moduleKeys = COMBO_MODULES[insurer].filter((module) => combo?.[module.key]).map((module) => module.key);

      if (insurer === 'bwho') {
        tobId = moduleKeys.includes('wmi') ? ['bwho', ...moduleKeys.filter((key) => key !== 'evacuation')].join('_') : null;
      } else {
        tobId = normalizeTobId(['ihhp', ...moduleKeys].join('_'));
      }
    } else if (MY_HEALTH_COMPARISON_INSURERS.includes(getBasePlan(selectedPlan))) {
      tobId = getMyHealthTobId(selectedPlan);
    } else if (isMyHealthComparison(selectedPlan) || !insurers_plans_map[getBasePlan(selectedPlan)]) {
      return null;
    } else {
      const basePlan = getBasePlan(selectedPlan);
      const plan = insurers_plans_map[basePlan].plan;
      const roomType = (selectedOptions || []).find((o) => o.insurer === 'pacific_cross_select' && o.type === plan + '_room_type')?.option_value || 'Ward';

      tobId = normalizeTobId(basePlan, { room_type: roomType });
    }

    return isUniversalTobId(tobId) ? tobId : null;
  }

  function getLoadedTob(tobId) {
    const data = tobTables[tobId];

    return data && Object.keys(data.table || {}).length > 0 ? data : null;
  }

  // Lays the column's edits, formatting and merges over its loaded TOB
  function withTobChanges(tob) {
    const table = Object.entries(tobCellPatches[tob.uid] || {}).reduce((acc, [rowNum, patch]) => patchCell(acc, rowNum, patch), tob.table);

    return { ...tob, table, vertical_merge_cells: tobMergeOverrides[tob.uid] ?? tob.vertical_merge_cells };
  }

  // A TOB picked from the Add TOB dialog: a custom TOB plan comes with its own table, the rest
  // are loaded by id. Undefined while still loading.
  function buildPickedTob(tob, uid) {
    if (tob.table) {
      return withTobChanges({ ...tob, uid });
    }

    if (tobTables[tob.id] === undefined) {
      return undefined;
    }

    const data = getLoadedTob(tob.id);

    return data
      ? withTobChanges({ ...tob, uid, table: data.table, vertical_merge_cells: data.vertical_merge_cells })
      : { id: tob.id, label: tob.label, uid, placeholder: true };
  }

  // A plan's TOB column, kept in step with the comparison: its Plan Type, Currency and Area of
  // Cover rows come from the plan, the way AutoQuotation fills them from the quoted premium.
  // Undefined while still loading.
  function buildPlanTob(selectedPlan) {
    if (filledTobs[selectedPlan]) {
      return buildPickedTob(filledTobs[selectedPlan], selectedPlan);
    }

    const label = getInsurerLabel(selectedPlan) + ' ' + getPlanLabel(selectedPlan);
    const tobId = getPlanTobId(selectedPlan);

    if (tobId && tobTables[tobId] === undefined) {
      return undefined;
    }

    const data = tobId && getLoadedTob(tobId);

    if (!data) {
      return { id: selectedPlan, label, uid: selectedPlan, placeholder: true };
    }

    let table = setTobTableRowValue(data.table, 2, getPlanLabel(selectedPlan));
    const planCurrency = getCurrency(selectedPlan);
    const areaOfCoverage = getAreaOfCoverage(selectedPlan);

    if (planCurrency) table = setTobTableRowValue(table, 4, planCurrency);
    if (areaOfCoverage) table = setTobTableRowValue(table, 5, normalizeAreaOfCoverDisplay(areaOfCoverage, DEFAULT_TOB_LANGUAGE));

    return withTobChanges({ id: tobId, label, uid: selectedPlan, table, vertical_merge_cells: data.vertical_merge_cells });
  }

  function buildTob(uid) {
    const addedTob = addedTobs.find((tob) => tob.uid === uid);

    return addedTob ? buildPickedTob(addedTob, uid) : buildPlanTob(uid);
  }

  // Every TOB id the TOB table needs loaded
  function getNeededTobIds() {
    const pickedTobs = [...addedTobs, ...Object.values(filledTobs)].filter((tob) => !tob.table).map((tob) => tob.id);
    const planTobIds = displayPlans.filter((plan) => !filledTobs[plan]).map(getPlanTobId).filter(Boolean);

    return [...new Set(['benefit_labels', ...planTobIds, ...pickedTobs])];
  }

  function handleOpenAddTobDialog() {
    setTobPlaceholderToFill(null);
    setAddTobOpen(true);
  }

  // The placeholder's Add button opens the same dialog, but the TOB picked there fills that
  // plan's column instead of adding a new one
  function handleOpenFillTobPlaceholder(placeholder) {
    setTobPlaceholderToFill(placeholder.uid);
    setAddTobOpen(true);
  }

  function handleCloseAddTobDialog() {
    setAddTobOpen(false);
    setTobPlaceholderToFill(null);
  }

  function handleSubmitAddTob(tob) {
    if (tobPlaceholderToFill) {
      setFilledTobs((prev) => ({ ...prev, [tobPlaceholderToFill]: tob }));
      return;
    }

    setAddedTobs((prev) => [...prev, { ...tob, uid: uuidv4() }]);
  }

  // Added TOBs are removed; a plan's own TOB can't be, as it follows the plan, but one picked
  // for a plan with no TOB goes back to the placeholder
  function getTobDeleteHandler(uid) {
    if (addedTobs.some((tob) => tob.uid === uid)) {
      return () => setAddedTobs((prev) => prev.filter((tob) => tob.uid !== uid));
    }

    if (filledTobs[uid]) {
      return () => setFilledTobs((prev) => {
        const next = { ...prev };
        delete next[uid];
        return next;
      });
    }

    return undefined;
  }

  function patchTobCells(uid, rowPatches) {
    setTobCellPatches((prev) => {
      const columnPatches = { ...(prev[uid] || {}) };

      Object.entries(rowPatches).forEach(([rowNum, patch]) => {
        columnPatches[rowNum] = { ...(columnPatches[rowNum] || {}), ...patch };
      });

      return { ...prev, [uid]: columnPatches };
    });
  }

  function handleUpdateTobCell(uid, rowNum, value) {
    patchTobCells(uid, { [rowNum]: { cell_value: value } });
  }

  // The toolbar shows the state of the first cell in the selection and applies it to all.
  function getSelectedTobCellData() {
    return tobSelection ? readCell(tobPlans.find((tob) => tob.uid === tobSelection.planUid)?.table, tobSelection.rows[0]) : null;
  }

  // patchCellStyle remembers each cell's original style the first time it changes, so the
  // style fields and that memory are what's kept for the cell
  function formatTobSelection(patch) {
    const tob = tobSelection && tobPlans.find((plan) => plan.uid === tobSelection.planUid);
    if (!tob) return;

    const rowPatches = {};

    tobSelection.rows.forEach((rowNum) => {
      const cell = readCell(patchCellStyle(tob.table, rowNum, patch), rowNum);

      rowPatches[rowNum] = Object.fromEntries([...Object.keys(patch), 'original_styles'].map((key) => [key, cell[key]]));
    });

    patchTobCells(tob.uid, rowPatches);
  }

  function getTobSelectionRange() {
    return tobSelection && {
      start: Math.min(...tobSelection.rows.map(Number)),
      end: Math.max(...tobSelection.rows.map(Number)),
    };
  }

  function getOverlappingTobMerges() {
    const range = getTobSelectionRange();

    return range
      ? (tobPlans.find((tob) => tob.uid === tobSelection.planUid)?.vertical_merge_cells || [])
        .filter((merge) => merge.end_row >= range.start && merge.start_row <= range.end)
      : [];
  }

  // Anything the new span overlaps is replaced by it, so the ranges never interleave.
  function updateTobMerges(updater) {
    const tob = tobPlans.find((plan) => plan.uid === tobSelection?.planUid);
    if (!tob) return;

    const range = getTobSelectionRange();
    const kept = (tob.vertical_merge_cells || []).filter((merge) => merge.end_row < range.start || merge.start_row > range.end);

    setTobMergeOverrides((prev) => ({ ...prev, [tob.uid]: updater(kept) }));
  }

  function handleMergeTob() {
    if (!(tobSelection?.rows.length > 1)) return;

    const range = getTobSelectionRange();

    updateTobMerges((kept) => [...kept, { col: 1, start_row: range.start, end_row: range.end }]
      .sort((a, b) => a.start_row - b.start_row));

    // Only the first row of a merge is rendered, so the rest can no longer be clicked.
    setTobSelection({ planUid: tobSelection.planUid, rows: [String(range.start)] });
  }

  function handleUnmergeTob() {
    if (getOverlappingTobMerges().length === 0) return;
    // updateTobMerges already drops every span the selection touches, so keeping only what
    // it hands back is the unmerge.
    updateTobMerges((kept) => kept);
  }

  function handleToggleTobModule(moduleName) {
    setSelectedTobModules((prev) => prev.includes(moduleName) ? prev.filter((name) => name !== moduleName) : [...prev, moduleName]);
  }

  function printPlansLabelMUI() {
    return sortedPlans().map(selectedPlan => {
      if (selectedPlan === 'my_health_indonesia') {
        if (!computedRates['my_health_indonesia']) return '';

        if (computedRates['my_health_indonesia'].length === 0) return '';

        return <TableCell key={selectedPlan} style={{ background: '#737373', color: '#ebe6e6', textAlign: 'center' }}>{computedRates['my_health_indonesia'][0]['plan_label']}</TableCell>;
      }

      if (selectedPlan === 'my_health_singapore') {
        if (!computedRates['my_health_singapore']) return '';

        if (computedRates['my_health_singapore'].length === 0) return '';

        return <TableCell key={selectedPlan} style={{ background: '#737373', color: '#ebe6e6', textAlign: 'center' }}>{computedRates['my_health_singapore'][0]['plan_label']}</TableCell>;
      }

      if (selectedPlan === 'my_health_vietnam') {
        if (!computedRates['my_health_vietnam']) return '';

        if (computedRates['my_health_vietnam'].length === 0) return '';

        return <TableCell key={selectedPlan} style={{ background: '#737373', color: '#ebe6e6', textAlign: 'center' }}>{computedRates['my_health_vietnam'][0]['plan_label']}</TableCell>;
      }

      if (selectedPlan === 'my_health_philippines') {
        if (!computedRates['my_health_philippines']) return '';

        if (computedRates['my_health_philippines'].length === 0) return '';

        return <TableCell key={selectedPlan} style={{ background: '#737373', color: '#ebe6e6', textAlign: 'center' }}>{computedRates['my_health_philippines'][0]['plan_label']}</TableCell>;
      }

      return <TableCell key={selectedPlan} style={{ background: '#737373', color: '#ebe6e6', textAlign: 'center' }}>{insurers_plans_map[selectedPlan].plan_label.toUpperCase()}</TableCell>
    })
  }

  // function paymentFrequencyLabel() {
  //   // specificPaymentFrequency
  //   // insurers

  //   const paymentFrequencyLabelMap = {
  //     'Annually': 'Annual',
  //     'Semi Annually': 'Semi Annual',
  //     'Quarterly': 'Quarterly',
  //     'Monthly': 'Monthly',
  //   };

  //   return paymentFrequencyLabelMap[paymentFrequency];
  // }

  function paymentFrequencyLabel() {
    const paymentFrequencyLabelMap = {
      'Annually': 'Annual',
      'Semi Annually': 'Semi Annual',
      'Quarterly': 'Quarterly',
      'Monthly': 'Monthly',
    };

    // Get all selected insurers (where value is true)
    const selectedInsurers = Object.entries(insurers)
      .filter(([_, value]) => value === true)
      .map(([key]) => key);

    // If no insurers are selected or only one is selected
    if (selectedInsurers.length <= 1) {
      if (selectedInsurers.length === 1) {
        const frequency = specificPaymentFrequency[selectedInsurers[0]];
        return paymentFrequencyLabelMap[frequency] || '';
      }
      return '';
    }

    // Check if all selected insurers have the same payment frequency
    const firstFrequency = specificPaymentFrequency[selectedInsurers[0]];
    const allSame = selectedInsurers.every(
      insurer => specificPaymentFrequency[insurer] === firstFrequency
    );

    // Return mapped frequency if all are the same, empty string otherwise
    return allSame ? paymentFrequencyLabelMap[firstFrequency] || '' : '';
  }

  // Lets the parent show how many plan columns are being compared (e.g. as a badge)
  const planCount = getDisplayPlans().length;
  const displayPlans = getOrderedPlans();
  const tobColumns = sortByPlanOrder([...displayPlans, ...addedTobs.map((tob) => tob.uid)]);
  const tobPlans = tobColumns.map(buildTob).filter(Boolean);
  const tobBenefitLabelsColumn = getLoadedTob('benefit_labels')?.table;
  const neededTobIds = getNeededTobIds();
  const loadingTobs = neededTobIds.some((tobId) => tobTables[tobId] === undefined);
  const selectedTobCellData = getSelectedTobCellData();
  const showTobMenuBar = tobEditing && tobTableHovered && (tobSelection?.rows.length || 0) > 0;
  const visibleTobRowNumbers = [1, 2, 3, 4, 5].map(String).concat(
    TOB_MODULES.filter((module) => selectedTobModules.includes(module.name)).flatMap((module) =>
      Array.from({ length: module.end - module.start + 1 }, (_, index) => String(module.start + index))
    )
  );

  // Splits a multi-plan TOB response into each plan's own single-column TOB (column '1'), the
  // shape a single-plan request returns
  function splitTobResponse(data, tobIds) {
    const planColumns = data?.plan_columns || {};

    return Object.fromEntries(tobIds.map((tobId) => {
      const col = planColumns[tobId];

      if (!col) {
        return [tobId, null];
      }

      const table = Object.fromEntries(Object.entries(data.table || {})
        .filter(([, row]) => row?.[col])
        .map(([rowNum, row]) => [rowNum, { '1': row[col] }]));
      const vertical_merge_cells = (data.vertical_merge_cells || [])
        .filter((merge) => merge.col === col)
        .map((merge) => ({ ...merge, col: 1 }));

      return [tobId, { table, vertical_merge_cells }];
    }));
  }

  // TOBs are only loaded once the Table Of Benefits accordion is opened, all the missing ones in
  // one request
  useEffect(() => {
    if (!open || !tobExpanded || !auth?.token) return;

    const tobIds = neededTobIds.filter((tobId) => tobTables[tobId] === undefined && !pendingTobFetchesRef.current.has(tobId));

    if (tobIds.length === 0) return;

    tobIds.forEach((tobId) => pendingTobFetchesRef.current.add(tobId));

    fetchTOBBenefits({ planIds: tobIds, language: DEFAULT_TOB_LANGUAGE, token: auth.token })
      .then((response) => response?.data)
      .catch((error) => {
        console.error('Failed to load TOB benefits:', error);
        return null;
      })
      .then((data) => {
        tobIds.forEach((tobId) => pendingTobFetchesRef.current.delete(tobId));
        setTobTables((prev) => ({ ...prev, ...splitTobResponse(data, tobIds) }));
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, tobExpanded, auth?.token, neededTobIds.join('|')]);

  const downloadPdf = async () => {
    setDownloadingPdf(true);
    try {
      const response = await downloadQuickQuotationPdf({
        quotation_table_html: getLiveTableHTML('Quotations', 'premiums-summary-pdf-container'),
        tob_table_html: getLiveTableHTML('Table of benefits', 'premiums-summary-tob-pdf-container'),
        styles: EMAIL_STYLES,
        provided_for: clients[0]?.full_name || '',
      }, auth?.token);

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${clients[0]?.full_name || 'Client'}_Quotation.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading PDF:', error);
      toast.error('Failed to download PDF. Please try again.');
    } finally {
      setDownloadingPdf(false);
    }
  };

  const handleDownloadPdf = () => {
    setTobExpanded(true);
    setPdfRequested(true);
  };

  // Downloads once the TOBs have loaded and rendered
  useEffect(() => {
    if (!pdfRequested || !tobExpanded || loadingTobs) return;

    setPdfRequested(false);
    downloadPdf();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pdfRequested, tobExpanded, loadingTobs]);

  useEffect(() => {
    if (onPlanCountChange) {
      onPlanCountChange(planCount);
    }
  }, [planCount]);

  function displaySpecificPaymentFrequency(selectedPlan) {
    // Check if paymentFrequencyLabel returns empty string
    if (paymentFrequencyLabel() === '') {
      const insurer = getPlanInsurer(selectedPlan);

      // If empty, return the raw frequency from specificPaymentFrequency for the selected insurer
      return '(' + specificPaymentFrequency[insurer] + ')';
    }
    // Otherwise return empty string
    return '';
  }

  return (
    <Fragment>
      <Dialog
        fullWidth
        maxWidth="xl"
        sx={{
          '& .MuiDialog-container': { alignItems: 'flex-start' },
          '& .MuiDialog-paper': { borderRadius: 4 },
          mt: 7
        }}
        open={open}
        onClose={handleClose}
        TransitionComponent={Transition}
      >
        <AppBar sx={{ position: 'relative' }}>
          <Toolbar>

            <Typography sx={{ ml: 2, flex: 1 }} variant="h6" component="div">
              Plan Comparison
            </Typography>
            <IconButton
              edge="start"
              color="inherit"
              onClick={handleClose}
              aria-label="close"
            >
              <CloseIcon />
            </IconButton>
          </Toolbar>
        </AppBar>
        <Container maxWidth={displayPlans.length <= 3 ? 'lg' : 'xl'} sx={{ pt: 4, pb: 3 }}>
          {/* <Typography sx={{ fontSize: 20, color: '#1976d2', mt: 3 }}>Options</Typography>
          <FormControlLabel control={<Checkbox checked={showDeductible} onChange={(e) => {
            setShowDeductible(e.target.checked);
          }} />} label="Show Deductible" /> */}
          <Box sx={{ mb: 2 }}>
            <Button
              variant="contained"
              size="small"
              startIcon={<DownloadIcon />}
              disabled={pdfRequested || downloadingPdf}
              onClick={handleDownloadPdf}
            >
              Download PDF
              {(pdfRequested || downloadingPdf) && <CircularProgress size={16} sx={{ color: '#fff', ml: 1 }} />}
            </Button>
          </Box>
          <Accordion
            defaultExpanded
            disableGutters
            // The top gap is the Container's padding: MUI zeroes the margin-top of an expanded first accordion
            sx={{ '&:before': { display: 'none' } }}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography sx={{ color: '#3f51b5', fontWeight: 700 }}>Premium Comparison</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <div style={{ display: 'flex', justifyContent: 'center', width: '100%' }}>

                <div style={{ display: 'flex', alignItems: 'start', justifyContent: 'space-between', gap: 16, width: '100%', marginBottom: 7 }}>
                  {/* Drag the chips to change the order of the plan columns */}
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                    <DndContext sensors={plansSensors} collisionDetection={closestCenter} onDragEnd={handlePlansDragEnd}>
                      <SortableContext items={displayPlans} strategy={horizontalListSortingStrategy}>
                        {displayPlans.map(selectedPlan => (
                          <SortablePlanChip
                            key={selectedPlan}
                            id={selectedPlan}
                            label={getInsurerLabel(selectedPlan) + ' ' + getPlanLabel(selectedPlan)}
                          />
                        ))}
                      </SortableContext>
                    </DndContext>
                  </div>
                  <div style={{ display: 'flex', flexShrink: 0 }}>
                  {/* <Button
                    sx={{ mb: 1 }}
                    onClick={() => {
                      var node = document.getElementById('table-premiums-summary');

                      domtoimage.toBlob(node).then(function (blob) {
                        // Create ClipboardItem with blob and its type, and add to an array
                        const data = [new ClipboardItem({ [blob.type]: blob })];
                        // Write the data to the clipboard
                        navigator.clipboard.write(data);

                        setShowImageCopy(true);
                      })
                        .catch(function (error) {
                          console.error('oops, something went wrong!', error);
                        });
                    }}
                    endIcon={<ContentCopyIcon />}
                  >Copy Image</Button> */}
                  <CopyExcelDataButton
                    elementId={'table-premiums-summary'}
                    style={{
                      marginRight: 7
                    }}
                  />
                  <CopyImageButton
                    elementId={'table-premiums-summary'}
                    setShowImageCopy={setShowImageCopy}
                    adjustWidth={false}
                  />
                  </div>
                </div>
              </div>


              <div id="premiums-summary-pdf-container" style={{ display: 'flex', justifyContent: 'center' }}>
                <table id="table-premiums-summary" className='table-premiums-summary' style={{ width: '100%', tableLayout: 'fixed', borderCollapse: 'collapse', background: '#fff', fontFamily: 'inherit', fontSize: 11 }}>
                  {/* The same first column as the TOB table, so each plan's premium sits right above its TOB */}
                  <colgroup>
                    <col style={{ width: TOB_FIRST_COLUMN_WIDTH }} />
                    {displayPlans.map((selectedPlan) => <col key={selectedPlan} />)}
                  </colgroup>
                  <tr style={{ background: '#050A30', color: '#ebe6e6', fontWeight: 700 }}>
                    <td colSpan={displayPlans.length + 1} style={{ paddingLeft: 20, paddingTop: 15, paddingBottom: 15 }}>PREMIUM COMPARISON</td>
                  </tr>
                  <tr style={{ background: '#050A30', color: '#ebe6e6', textAlign: 'center', fontWeight: 700 }}>
                    <td rowSpan={getPlanLevelRowSpan()}>PLAN LEVEL</td>
                    {
                      displayPlans.map(selectedPlan => {
                        return <td key={selectedPlan}>{getInsurerLabel(selectedPlan)}</td>
                      })
                    }
                  </tr>
                  {
                    <tr style={{ background: '#737373', color: '#ebe6e6', textAlign: 'center', fontWeight: 700 }}>
                      {
                        displayPlans.map(selectedPlan => {
                          const insurer = getPlanInsurer(selectedPlan);

                          let area_of_coverage = specificAreaOfCoverage[insurer] || '';

                          if (insurer === 'hsbc_smart_care_optimum_enhanced' && !area_of_coverage) {
                            area_of_coverage = 'Singapore';
                          }

                          return <td key={selectedPlan} style={{ fontSize: area_of_coverage.length > 20 ? 12 : null }}>{area_of_coverage}</td>
                        })
                      }
                    </tr>
                  }
                  {
                    showDeductible &&
                    <tr style={{ background: '#737373', color: '#ebe6e6', textAlign: 'center', fontWeight: 700 }}>
                      {
                        displayPlans.map(selectedPlan => {
                          let deductible = getDeductible(selectedPlan);

                          // Morgan Price shows its excess as-is ('Nil Excess', '$100 Excess'), not as a deductible
                          if (getPlanInsurer(selectedPlan) !== 'morgan_price') {
                            deductible = zeroDeductibleUniform(deductible);
                          }

                          if (!deductible) {
                            return <td key={selectedPlan}>N/A</td>
                          }

                          return <td key={selectedPlan} style={{ fontSize: deductible.length > 20 ? 12 : null }}>{deductible}</td>
                        })
                      }
                    </tr>
                  }
                  <tr style={{ background: '#737373', color: '#ebe6e6', textAlign: 'center', fontWeight: 700 }}>
                    {/* {
                      sortedPlans().map(selectedPlan => {
                        return <td>{insurers_plans_map[selectedPlan].plan_label.toUpperCase()}</td>
                      })
                    } */}
                    {
                      printPlansLabel()
                    }
                  </tr>
                  {
                    clients.map((client, index) => {
                      return <tr key={client.id} style={{ background: '#EDEDED', color: '#050A30', textAlign: 'center' }}>
                        <td>{client.full_name} ({client.age}) {client.country_of_residence}</td>

                        {
                          displayPlans.map(selectedPlan => {
                            const rate = isCombo(selectedPlan) ? getComboRateByKey(selectedPlan, index) : getRate(selectedPlan, index);

                            return <td key={selectedPlan}>{formatRate(selectedPlan, getCurrency(selectedPlan), rate)}</td>
                          })
                        }

                      </tr>
                    })

                  }
                  <tr style={{ background: '#737373', color: '#ebe6e6', textAlign: 'center', fontWeight: 700 }}>
                    <td>Total Indicative {paymentFrequencyLabel()} Premium</td>
                    {
                      displayPlans.map(selectedPlan => {
                        const subtotal = isCombo(selectedPlan) ? getComboSubtotalByKey(selectedPlan) : getSubtotalRate(selectedPlan);

                        return <td key={selectedPlan}>{formatRate(selectedPlan, getCurrency(selectedPlan), subtotal)} {displaySpecificPaymentFrequency(selectedPlan)}</td>
                      })
                    }
                  </tr>
                </table>
              </div>
            </AccordionDetails>
          </Accordion>

          {/* Each plan above has its TOB column here, in the same order */}
          <Accordion
            expanded={tobExpanded}
            onChange={(_, expanded) => setTobExpanded(expanded)}
            disableGutters
            sx={{ mt: 3, '&:before': { display: 'none' } }}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography sx={{ color: '#3f51b5', fontWeight: 700 }}>Table Of Benefits</Typography>
              <IconButton
                size="small"
                title={tobEditing ? 'Stop editing' : 'Edit'}
                onClick={(event) => {
                  // Keeps the click from toggling the accordion; turning editing on also opens it
                  event.stopPropagation();
                  if (tobEditing) setTobSelection(null);
                  else setTobExpanded(true);
                  setTobEditing(!tobEditing);
                }}
                sx={{ ml: 1, p: 0.5, color: tobEditing ? '#fff' : '#3f51b5', backgroundColor: tobEditing ? '#3f51b5' : 'transparent', '&:hover': { backgroundColor: tobEditing ? '#303f9f' : 'rgba(63, 81, 181, 0.08)' } }}
              >
                <EditIcon sx={{ fontSize: 18 }} />
              </IconButton>
              {tobExpanded && loadingTobs && <CircularProgress size={18} sx={{ ml: 2 }} />}
            </AccordionSummary>
            <AccordionDetails onMouseEnter={() => setTobTableHovered(true)} onMouseLeave={() => setTobTableHovered(false)}>
              {tobEditing && tobPlans.length > 0 && (
                <TOBModuleOptions
                  modules={TOB_MODULES}
                  selectedModules={selectedTobModules}
                  onToggleModule={handleToggleTobModule}
                />
              )}
              {tobEditing && (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                <DndContext sensors={tobPlansSensors} collisionDetection={closestCenter} onDragEnd={handleTobPlansDragEnd}>
                  <SortableContext items={tobColumns} strategy={horizontalListSortingStrategy}>
                    {tobColumns.map((uid) => (
                      <SortablePlanChip
                        key={uid}
                        id={uid}
                        label={(addedTobs.find((tob) => tob.uid === uid) || filledTobs[uid])?.label || getInsurerLabel(uid) + ' ' + getPlanLabel(uid)}
                        onDelete={getTobDeleteHandler(uid)}
                      />
                    ))}
                  </SortableContext>
                </DndContext>
                <Chip size="small" variant="outlined" icon={<AddIcon sx={{ color: '#7e57c2 !important' }} />} label="Add TOB" onClick={handleOpenAddTobDialog} sx={{ backgroundColor: '#f5f5f5', color: '#7e57c2', borderStyle: 'dashed', borderColor: '#7e57c2', borderWidth: 1 }} />
              </Box>
              )}
              {tobEditing && tobPlans.length > 0 && (
                <Box sx={{ position: 'sticky', top: 0, zIndex: 2, backgroundColor: '#fff', transition: 'opacity 150ms', ...(!showTobMenuBar && { opacity: 0, visibility: 'hidden', pointerEvents: 'none' }) }}>
                  <TOBTableMenuBar
                    cell={selectedTobCellData}
                    selectedCount={tobSelection?.rows.length || 0}
                    canMerge={(tobSelection?.rows.length || 0) > 1}
                    canUnmerge={getOverlappingTobMerges().length > 0}
                    onToggleBold={() => formatTobSelection({ font_weight: isBoldCell(selectedTobCellData) ? '400' : '700' })}
                    onSetTextColor={(color) => formatTobSelection({ cell_text_color: color })}
                    onSetBackgroundColor={(color) => formatTobSelection({ cell_bg_color: color })}
                    onMerge={handleMergeTob}
                    onUnmerge={handleUnmergeTob}
                  />
                </Box>
              )}
              <div id="premiums-summary-tob-pdf-container">
                <TOBTable
                  tobPlans={tobPlans}
                  language={DEFAULT_TOB_LANGUAGE}
                  tobBenefitLabelsColumn={tobBenefitLabelsColumn}
                  visibleRowNumbers={visibleTobRowNumbers}
                  editable
                  readOnly={!tobEditing}
                  onUpdateCell={handleUpdateTobCell}
                  selection={tobSelection}
                  onSelectCells={setTobSelection}
                  onAddPlaceholderTob={handleOpenFillTobPlaceholder}
                />
              </div>
            </AccordionDetails>
          </Accordion>
          {/* <TableContainer id="table-premiums-summary"  component={Paper}>
            <Table size='small' sx={{ minWidth: 650, width: '100%', tableLayout: 'fixed' }} aria-label="simple table">
              <TableHead sx={{ background: '#050A30' }}>
                <TableRow>
                  <TableCell rowSpan={3} sx={{ color: '#ebe6e6', textAlign: 'center' }}>PLAN LEVEL</TableCell>
                  {
                    sortedPlans().map(selectedPlan => {
                      return <TableCell sx={{ color: '#ebe6e6', textAlign: 'center' }}>{insurers_plans_map[selectedPlan].insurer_label}</TableCell>
                    })
                  }
                </TableRow>
                <TableRow>
                  {
                    sortedPlans().map(selectedPlan => {
                      let deductible = getDeductible(selectedPlan);

                      deductible = zeroDeductibleUniform(deductible);

                      return <TableCell sx={{ background: '#737373', color: '#ebe6e6', textAlign: 'center' }} style={{ fontSize: deductible.length > 20 ? 12 : null }}>{deductible}</TableCell>
                    })
                  }
                </TableRow>
                <TableRow>
                  {
                    printPlansLabelMUI()
                  }
                </TableRow>
              </TableHead>
              <TableBody>
                {
                  clients.map((client, index) => {
                    return <TableRow>
                      <TableCell sx={{ background: '#EDEDED', color: '#050A30', textAlign: 'center' }}>{client.full_name.toUpperCase()} ({client.age}) {client.country_of_residence.toUpperCase()}</TableCell>

                      {
                        sortedPlans().map(selectedPlan => {
                          return <TableCell sx={{ background: '#EDEDED', color: '#050A30', textAlign: 'center' }}>{formatRate(selectedPlan, currency, getRate(selectedPlan, index))}</TableCell>
                        })
                      }

                    </TableRow>
                  })

                }

                <TableRow>
                  <TableCell style={{ background: '#737373', color: '#ebe6e6', textAlign: 'center' }}>TOTAL INDICATIVE ANNUAL PREMIUM</TableCell>
                  {
                    sortedPlans().map(selectedPlan => {
                      return <TableCell style={{ background: '#737373', color: '#ebe6e6', textAlign: 'center' }}>{formatRate(selectedPlan, currency, getSubtotalRate(selectedPlan))}</TableCell>
                    })
                  }
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer> */}
        </Container>
      </Dialog>

      <AddTobDialog
        open={addTobOpen}
        onClose={handleCloseAddTobDialog}
        onSubmit={handleSubmitAddTob}
        existingPlanIds={tobPlans.map((tob) => tob.id)}
        token={auth?.token}
      />

      <Snackbar
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        open={showImageCopy}
        autoHideDuration={1000}
        onClose={handleCloseSnackbar}
        message="Image Copied to Clipboard"
      />
    </Fragment>
  );
}